/**
 * Sizes a WebGPU canvas to its container, at the display's own pixel density.
 *
 * `useChartCanvas` cannot be reused for this: it takes a 2D context and paints through it, and a
 * canvas can only ever have one context - asking for `'2d'` after `'webgpu'` returns null. What
 * carries over is everything it learned about sizing, and it is all repeated here for the same
 * reasons: a `ResizeObserver` because these views live in a resizable pane, a device-pixel backing
 * store because a canvas sized in CSS pixels renders soft on a retina display, and a
 * `matchMedia('(resolution: Ndppx)')` watch because dragging a window between two displays changes
 * the ratio without resizing anything.
 *
 * What it does not carry over is the painting. A GPU frame is scheduled by the caller, which has
 * more than the size to react to.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type RefObject,
} from 'react';
import type { Size } from '../../types';

const EMPTY_SIZE: Size = { width: 0, height: 0 };

/**
 * Ceiling on the backing store, in device pixels on either axis.
 *
 * Every WebGPU adapter reports at least 8192 for `maxTextureDimension2D`, and a render attachment
 * larger than it is a validation error rather than a downgrade. Reachable in practice: a 5K display
 * at a 2x ratio is 10240 physical pixels across.
 */
const MAXIMUM_ATTACHMENT = 8192;

export type GpuCanvasSizing = {
  containerRef: (element: HTMLDivElement | null) => void;
  /** CSS pixels, which is what pointer coordinates and hit-testing are in. */
  viewport: Size;
  /** Device pixels, matching the backing store, which is what the renderer is given. */
  deviceViewport: Size;
  pixelRatio: number;
};

export const useGpuCanvas = (
  canvasRef: RefObject<HTMLCanvasElement | null>
): GpuCanvasSizing => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<Size>(EMPTY_SIZE);
  const [pixelRatio, setPixelRatio] = useState(() =>
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  );

  const containerRef = useCallback((element: HTMLDivElement | null) => {
    setContainer(element);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    /* Re-armed each time, because the query is written against the ratio currently in force and
       stops matching the moment it changes. */
    const query = window.matchMedia(`(resolution: ${pixelRatio}dppx)`);
    const onChange = () => setPixelRatio(window.devicePixelRatio || 1);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [pixelRatio]);

  useLayoutEffect(() => {
    if (!container) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      const next = { width: Math.round(width), height: Math.round(height) };

      /* A value-equal size leaves the state alone: `contentRect` is fractional and this rounds, so
         a sub-pixel change during a pane drag would otherwise hand out a fresh object and
         reallocate both multisample attachments for a viewport already drawn. */
      setViewport(current =>
        current.width === next.width && current.height === next.height
          ? current
          : next
      );
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [container]);

  /**
   * Memoised so its identity tracks its value. A caller's draw effect depends on it, and a fresh
   * object per render would schedule a frame on every unrelated re-render - a hover among them.
   *
   * Floored at one pixel because a render attachment of zero is invalid, which means **a caller
   * cannot test this for zero**: it is never zero. Guard the draw on `viewport` instead, as the
   * sizing effect below does - otherwise a container that measures zero leaves the canvas at its
   * default 300x150 while the draw runs at 1x1, and the resolve-target mismatch that follows is a
   * validation error rather than a small frame.
   */
  const deviceViewport = useMemo<Size>(
    () => ({
      width: Math.min(
        MAXIMUM_ATTACHMENT,
        Math.max(1, Math.round(viewport.width * pixelRatio))
      ),
      height: Math.min(
        MAXIMUM_ATTACHMENT,
        Math.max(1, Math.round(viewport.height * pixelRatio))
      ),
    }),
    [viewport.width, viewport.height, pixelRatio]
  );

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || viewport.width === 0 || viewport.height === 0) return;

    /* Guarded, because assigning either dimension reallocates the swap chain. During an orbit the
       size never changes, and reallocating at every frame is pure waste. */
    if (
      canvas.width !== deviceViewport.width ||
      canvas.height !== deviceViewport.height
    ) {
      canvas.width = deviceViewport.width;
      canvas.height = deviceViewport.height;
    }

    /* Outside the guard: a canvas with no `style.width` lays out at its intrinsic 300x150, and the
       assignments are idempotent, so paying them every time costs nothing. */
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
  }, [canvasRef, viewport, deviceViewport]);

  return { containerRef, viewport, deviceViewport, pixelRatio };
};
